import { useState, useEffect, useCallback, useRef } from 'react';
import * as signalR from '@microsoft/signalr';

/**
 * Custom hook for tracking progress with SignalR real-time updates
 *
 * @param {Object} config - Configuration object
 * @param {string} config.hubUrl - The SignalR hub URL
 * @param {string} config.requestId - The request ID for the hub connection
 * @param {Array} config.steps - Array of step objects
 * @param {boolean} config.enabled - Whether to enable the connection (default: true)
 * @param {boolean} config.enforceStepOrder - Whether to enforce Math.max logic (default: true)
 * @param {boolean} config.trackCompletedSteps - Track completed steps separately (for onboarding)
 * @returns {Object} Progress tracking state and utilities
 */
function useProgressTracking({
  hubUrl,
  requestId,
  steps = [],
  enabled = true,
  enforceStepOrder = true,
  trackCompletedSteps = false,
  stopWhenTerminal = false,
}) {
  const [currentStepId, setCurrentStepId] = useState(() => {
    const currentStep = steps.find((step) => step.isCurrentStep);
    return currentStep?.id || steps[0]?.id || null;
  });
  const [progress, setProgress] = useState(0);
  const [completedStepIds, setCompletedStepIds] = useState(new Set());
  const [isCompleted, setIsCompleted] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [stepMessages, setStepMessages] = useState(new Map());
  const [isHubConnected, setIsHubConnected] = useState(false);

  const stepsRef = useRef(steps);
  const currentStepIdRef = useRef(currentStepId);
  const enforceStepOrderRef = useRef(enforceStepOrder);
  const trackCompletedStepsRef = useRef(trackCompletedSteps);
  const pendingEventsRef = useRef(new Map());
  const processedStepsRef = useRef(new Set());
  const isCompletedRef = useRef(isCompleted);
  const hasEverBeenCompletedRef = useRef(false);

  useEffect(() => {
    stepsRef.current = steps;
    currentStepIdRef.current = currentStepId;
    enforceStepOrderRef.current = enforceStepOrder;
    trackCompletedStepsRef.current = trackCompletedSteps;
    isCompletedRef.current = isCompleted;
    if (isCompleted) {
      hasEverBeenCompletedRef.current = true;
    }
  }, [steps, currentStepId, enforceStepOrder, trackCompletedSteps, isCompleted]);

  const updateCurrentStepId = useCallback((incomingStepId) => {
    if (!incomingStepId || stepsRef.current.length === 0) return;

    const incomingIndex = stepsRef.current.findIndex(
      (s) => String(s.id).toLowerCase() === String(incomingStepId).toLowerCase(),
    );
    if (incomingIndex === -1) {
      return;
    }

    const normalizedIncomingId = stepsRef.current[incomingIndex]?.id ?? incomingStepId;

    if (trackCompletedStepsRef.current) {
      setCurrentStepId((prevStepId) => {
        if (prevStepId && prevStepId !== normalizedIncomingId) {
          setCompletedStepIds((prev) => new Set([...prev, prevStepId]));
        }
        return normalizedIncomingId;
      });
      return;
    }

    if (!enforceStepOrderRef.current) {
      setCurrentStepId(normalizedIncomingId);
      return;
    }

    const currentIndex = stepsRef.current.findIndex(
      (s) => String(s.id).toLowerCase() === String(currentStepIdRef.current).toLowerCase(),
    );

    if (incomingIndex > currentIndex) {
      setCurrentStepId(normalizedIncomingId);
      processedStepsRef.current.add(normalizedIncomingId);
    } else if (currentIndex === -1) {
      setCurrentStepId(normalizedIncomingId);
      processedStepsRef.current.add(normalizedIncomingId);
    } else if (incomingIndex < currentIndex && !processedStepsRef.current.has(normalizedIncomingId)) {
      pendingEventsRef.current.set(normalizedIncomingId, incomingIndex);
    }
  }, []);

  const processPendingEvents = useCallback(() => {
    const currentIndex = stepsRef.current.findIndex((s) => s.id === currentStepIdRef.current);
    if (currentIndex === -1) return;

    const sortedPendingEvents = Array.from(pendingEventsRef.current.entries()).sort(([, a], [, b]) => a - b);

    const nextSequentialEvent = sortedPendingEvents.find(([, stepIndex]) => stepIndex === currentIndex + 1);

    if (nextSequentialEvent) {
      const [stepId] = nextSequentialEvent;
      setCurrentStepId(stepId);
      processedStepsRef.current.add(stepId);
      pendingEventsRef.current.delete(stepId);
    }
  }, []);

  const hasConnectedRef = useRef(false);

  useEffect(() => {
    if (steps && steps.length > 0) {
      const currentStep = steps.find((step) => step.isCurrentStep);
      if (currentStep?.id && currentStep.id !== currentStepId) {
        if (!enabled || !hasConnectedRef.current) {
          updateCurrentStepId(currentStep.id);
        }
      }
    }
  }, [steps, currentStepId, enabled]);

  useEffect(() => {
    processPendingEvents();
  }, [currentStepId, processPendingEvents]);

  useEffect(() => {
    if (steps && steps.length > 0) {
      if (trackCompletedSteps) {
        const completedCount = completedStepIds.size;
        const hasActiveStep = currentStepId ? 1 : 0;
        const progressSteps = completedCount + hasActiveStep;
        const progressValue = (progressSteps / steps.length) * 100;
        setProgress(Math.min(progressValue, 100));
      } else if (currentStepId) {
        const foundIndex = steps.findIndex((step) => step.id === currentStepId);
        if (foundIndex !== -1) {
          const progressValue = ((foundIndex + 1) / steps.length) * 100;
          setProgress(progressValue);
        }
      }
    }
  }, [currentStepId, steps, trackCompletedSteps, completedStepIds]);

  useEffect(() => {
    if (isCompleted || hasError) {
      setProgress(100);
    }
  }, [isCompleted, hasError]);

  useEffect(() => {
    const shouldConnect = Boolean(
      hubUrl && requestId && enabled && !(stopWhenTerminal && (isCompleted || hasError)),
    );

    if (!shouldConnect) {
      hasConnectedRef.current = false;
      setIsHubConnected(false);
      return undefined;
    }

    let disposed = false;
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${hubUrl}?requestId=${requestId}`)
      .withAutomaticReconnect()
      .configureLogging(process.env.NODE_ENV === 'development' ? signalR.LogLevel.Debug : signalR.LogLevel.Information)
      .build();

    connection.onreconnecting(() => {
      hasConnectedRef.current = false;
      if (!disposed) {
        setIsHubConnected(false);
      }
    });

    connection.onreconnected(() => {
      if (!disposed) {
        hasConnectedRef.current = true;
        setIsHubConnected(true);
      }
    });

    connection.onclose(() => {
      hasConnectedRef.current = false;
      if (!disposed) {
        setIsHubConnected(false);
      }
    });

    connection
      .start()
      .then(() => {
        if (!disposed) {
          hasConnectedRef.current = true;
          setIsHubConnected(true);
        }
      })
      .catch(() => {
        hasConnectedRef.current = false;
        if (!disposed) {
          setIsHubConnected(false);
        }
      });

    connection.on('message', (msg) => {
      try {
        const notification = typeof msg === 'string' ? JSON.parse(msg) : msg;

        const stepId = notification.currentStepId || notification.stepId || notification.id;

        if (notification.isCompleted !== undefined) {
          if (notification.isCompleted || !hasEverBeenCompletedRef.current) {
            setIsCompleted(notification.isCompleted);
          }
          if (trackCompletedStepsRef.current && notification.isCompleted && stepId) {
            setCompletedStepIds((prev) => new Set([...prev, stepId]));
          }
        }

        if (notification.hasError !== undefined) {
          setHasError(notification.hasError);
        }

        if (notification.error || notification.errorMessage) {
          setErrorMessage(notification.error || notification.errorMessage);
        }

        if (notification.message && stepId) {
          setStepMessages((prev) => new Map(prev).set(stepId, notification.message));
        }

        if (notification.isCompleted || notification.hasError) {
          if (stepId) {
            setCurrentStepId(stepId);
            processedStepsRef.current.add(stepId);
          }
        } else if (stepId && !hasEverBeenCompletedRef.current) {
          updateCurrentStepId(stepId);
        }
      } catch {
        // Ignore malformed notifications
      }
    });

    return () => {
      disposed = true;
      hasConnectedRef.current = false;
      setIsHubConnected(false);
      connection.stop();
    };
  }, [hubUrl, requestId, enabled, stopWhenTerminal, isCompleted, hasError, updateCurrentStepId]);

  return {
    currentStepId,
    progress,
    completedStepIds,
    isCompleted,
    hasError,
    errorMessage,
    stepMessages,
    isHubConnected,
    setCurrentStepId,
    setIsCompleted,
    setHasError,
    setErrorMessage,
    setStepMessages,
  };
}

export default useProgressTracking;
